import {
  CALENDAR_HEIGHT_COMFORTABLE,
  CALENDAR_HEIGHT_COMPACT,
  CALENDAR_HEIGHT_DEFAULT,
  CalendarDensity,
  CalendarPositioner,
} from "@clockwise/web-commons/src/components/calendar";
import { CalendarAvailability } from "@clockwise/web-commons/src/components/calendar/calendar-availability/CalendarAvailability";
import { CalendarConflicts } from "@clockwise/web-commons/src/components/calendar/calendar-conflicts/CalendarConflicts";
import { CalendarTimeDial } from "@clockwise/web-commons/src/components/calendar/calendar-time-dial/CalendarTimeDial";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { useReadCalendar } from "@clockwise/web-commons/src/util/CalendarContext";
import { showWeeksWorkHourBoundaries } from "@clockwise/web-commons/src/util/local-storage";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { animated, useSpring } from "@react-spring/web";
import { isEqual } from "lodash";
import { DateTime, Interval } from "luxon";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useReadLocalStorage } from "usehooks-ts";
import { IReduxState } from "../../../state/reducers/root.reducer";
import { useUpdateActiveEventDiff } from "../../chat-plus-calendar/util/ActiveDiffContext";
import { ConflictClusterWithConversationId } from "../../chat-plus-calendar/util/parseConflictClustersGQL";
import { useUserProfile } from "../../hooks/useUserProfile";
import { RescheduleConfirmationWrapper } from "../../reschedule-confirmation-modal";
import { PlannerContext } from "../Context";
import { ConflictPopover } from "../calendar-popover/ConflictPopover";
import { useAvailablities } from "../hooks/useAvailablities";
import { OOOEventsUnderlay } from "../ooo-events/OOOEventsUnderlay";
import DateTimeMarker from "../planner-datetime-marker/DateTimeMarker";
import { PlannerEvents } from "../planner-event";
import { PlannerHead } from "../planner-head/PlannerHead";
import PlannerMask from "../planner-masks/PlannerMask";
import { TimeSuggestion } from "../time-suggestion";
import { CalendarScrollSync } from "./CalendarScrollSync";
import { dateToDayOfWeek } from "./utils/dateToDayOfWeek";
import { getCardsFromCardsByDay } from "./utils/getCardsFromCardsByDay";
import { parseWorkingHourBounds } from "./utils/parseWorkingHourBounds";

type Next = (_: Record<string, string | number | boolean>) => Promise<unknown>;

export const CalendarWeek = () => {
  const { calendarIds, allDayEvents: allDayEventsNullable, conflictClusters } = useContext(
    PlannerContext,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [activeConflict, setActiveConflict] = useState<ConflictClusterWithConversationId | null>(
    null,
  );
  const { userProfile } = useUserProfile();

  const calendarState = useReadCalendar();
  const updateActiveEvent = useUpdateActiveEvent();
  const updateActiveEventDiff = useUpdateActiveEventDiff();

  const [datesToRender, setDatesToRender] = useState<string[]>(calendarState.visibleDates);
  const zone = getRenderTimeZone();
  const dateTimesToRender = React.useMemo(
    () => datesToRender.map((d) => DateTime.fromISO(d, { zone })),
    [datesToRender, zone],
  );

  const availablities = useAvailablities({ dateTimes: dateTimesToRender, minCalendarCount: 2 });
  const calendarDensity = useReadLocalStorage<CalendarDensity>("calendarDensity");

  let calHeight = CALENDAR_HEIGHT_DEFAULT;
  if (calendarDensity === "compact") {
    calHeight = CALENDAR_HEIGHT_COMPACT;
  } else if (calendarDensity === "comfortable") {
    calHeight = CALENDAR_HEIGHT_COMFORTABLE;
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>, id: string) => {
    if (!conflictClusters?.length) return;
    setAnchorEl(event.currentTarget);
    const conflictCluster = conflictClusters.find((c) => c.id === id);
    setActiveConflict(conflictCluster || null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveConflict(null);
  };

  useEffect(() => {
    // if transition is day <-> week, update without animation
    if (datesToRender.length !== calendarState.visibleDates.length) {
      setDatesToRender(calendarState.visibleDates);
      return;
    }

    // if the render dates are the same, don't animate
    if (isEqual(datesToRender, calendarState.visibleDates)) {
      return;
    }

    // determine if we should animate forward or backward
    const currentFirstRenderDateTime = dateTimesToRender[0];
    const newFirstRenderDateTime = DateTime.fromISO(calendarState.visibleDates[0]);
    const stepForward = newFirstRenderDateTime > currentFirstRenderDateTime;

    // animate the calendar, and update local state
    animateCalendar(stepForward, () => {
      setDatesToRender(calendarState.visibleDates);
    });
  }, [calendarState.visibleDates]);

  // The reason why we need `visibleDateTimes` is because without it, the <PlannerHead /> component
  // and <AllDayEvents /> component will flicker after changing the date range to previous or next week.
  // This is because the `calendarState.visibleDates` are updating only after an animation is completed.
  // Thus we need to calculate the date times to render immediately to avoid flickering.
  const visibleDateTimes = calendarState.visibleDates.map((d) => DateTime.fromISO(d, { zone }));

  // DevNote: we may look to lift this logic up to provider in future as this data format may no longer be needed
  const allDayEventCards = getCardsFromCardsByDay(allDayEventsNullable, visibleDateTimes);

  const highlightedSlotOnPlanner = useSelector(
    (state: IReduxState) => state.plannerHighlights.slot,
  );
  const slotMeetingTitle = useSelector(
    (state: IReduxState) => state.plannerHighlights.meetingTitle,
  );
  const additionalChanges = highlightedSlotOnPlanner?.additionalChanges || [];

  const highlightStart = highlightedSlotOnPlanner
    ? DateTime.fromISO(highlightedSlotOnPlanner.timeRange.startTime.dateOrDateTime)
    : undefined;
  const highlightEnd = highlightedSlotOnPlanner
    ? DateTime.fromISO(highlightedSlotOnPlanner.timeRange.endTime.dateOrDateTime)
    : undefined;
  const [animateCalStyles, animateCal] = useSpring(() => ({}));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  const animateCalendar = useCallback(
    (goForward: boolean, goToNewDate: () => void) => {
      const distance = 16, // React spring seems to do best with an even number here.
        duration = 135;
      animateCal.start(() => ({
        to: async (next: Next) => {
          await next({ translateX: -distance * (goForward ? 1 : -1), opacity: 0.25 });
          await next({
            translateX: distance * 1.8 * (goForward ? 1 : -1),
            opacity: 0,
            immediate: true,
          });
          await next({ immediate: true, opacity: 0.5 });
          goToNewDate();
          await next({ translateX: 0, opacity: 1 });
        },
        config: { duration },
      }));
    },
    [animateCal],
  );

  if (!calendarIds) {
    return null;
  }

  const handleSelectEvent = (calendarId: string, externalEventId: string) => {
    updateActiveEventDiff(null);
    updateActiveEvent({ calendarId, externalEventId });
  };

  return (
    <animated.div
      style={animateCalStyles}
      className="cw-flex cw-flex-1 cw-flex-col cw-mt-6 cw-w-[calc(100%-12px)] cw-h-full"
    >
      <div className="cw-relative cw-flex-1 cw-w-full cw-h-full cw-flex cw-flex-col cw-my-0 cw-mx-3 cw-pb-3">
        <div className="cw-border-b cw-border-subtle cw-border-solid">
          <div className="cw-w-[40px]"></div>
          <PlannerHead
            dateTimes={visibleDateTimes}
            events={allDayEventCards}
            onEventClick={handleSelectEvent}
          />
        </div>
        <div className="cw-overflow-auto cw-mb-[66px] cw-flex-grow" ref={scrollContainerRef}>
          <div
            style={{ minHeight: calHeight }}
            className="cw-relative cw-flex cw-flex-row cw-flex-1 cw-h-full"
            ref={calendarContainerRef}
          >
            {/* Non-rendering component that adjusts the scrollTop based on:
                1. On first mount, any previous scroll events initiated by the user
                2. The current time on routing to /app/ai-scheduler (via extension)
                3. The earliest start time in a conversation (proposal or viewEvents)
             */}
            <CalendarScrollSync
              scrollContainerRef={scrollContainerRef}
              calendarContainerRef={calendarContainerRef}
            />
            <CalendarTimeDial />
            <div className="cw-flex cw-flex-row cw-flex-1 cw-ml-5 cw-relative cw-h-[calc(100%+1px)]">
              <CalendarAvailability dateTimes={dateTimesToRender} availablities={availablities} />
              <OOOEventsUnderlay dateTimes={dateTimesToRender} />
              <CalendarHorizontalGuideLines />
              <CalendarWorkHourBounds />
              <CalendarVerticalGuideLines dateTimes={dateTimesToRender} />
              <DateTimeMarker daysRendered={dateTimesToRender} />
              <PlannerEvents dateTimes={dateTimesToRender} />
              {highlightStart && highlightEnd && (
                <PlannerMask
                  key="selected-slot"
                  type="selected-slot"
                  start={highlightStart}
                  end={highlightEnd}
                  text={
                    slotMeetingTitle ||
                    `${highlightStart.toFormat("h:mm")} - ${highlightEnd.toFormat("h:mm")}`
                  }
                />
              )}
              <CalendarConflicts
                dateTimes={dateTimesToRender}
                activeConflictId={activeConflict?.id}
                conflictClusters={conflictClusters || []}
                onNodeClick={handleClick}
              />
              {additionalChanges.map(({ event, to }) => {
                const { events } = event;
                const oneOfTheEvents = events[0];
                const { startTime, endTime } = oneOfTheEvents;
                const duration = DateTime.fromISO(endTime.dateOrDateTime)
                  .diff(DateTime.fromISO(startTime.dateOrDateTime))
                  .as("milliseconds");

                return (
                  <PlannerMask
                    key={`suggested-move-${event.id}`}
                    type="suggested-move"
                    start={DateTime.fromISO(to)}
                    end={DateTime.fromISO(to).plus({ milliseconds: duration })}
                    text={events[0].title}
                  />
                );
              })}
              <ConflictPopover
                activeConflict={activeConflict}
                anchorEl={anchorEl}
                onClose={handleClose}
                calendarId={userProfile?.primaryEmail || ""}
              />
              <TimeSuggestion dateTimes={dateTimesToRender} />
              <RescheduleConfirmationWrapper />
            </div>
          </div>
        </div>
      </div>
    </animated.div>
  );
};

const CalendarWorkHourBounds = () => {
  const { workingHours } = useContext(PlannerContext);
  const { visibleDates } = useReadCalendar();
  const visibleDays = visibleDates.map((date) => DateTime.fromFormat(date, "yyyy-MM-dd"));
  const visibleDaysOfWeek = visibleDays.map(dateToDayOfWeek);

  // This is an internal feature only used for testing calendar camera position.
  // It makes it easier to see the boundaries of the work hours.
  // If you want to turn it on, go to local storage and set the key "ShowWeeksWorkHourBoundaries" to `true` manually.
  const shouldShowWeeksWorkHourBoundaries = showWeeksWorkHourBoundaries.get();

  if (!shouldShowWeeksWorkHourBoundaries) {
    return null;
  }

  const daySettingsForVisibleDaysOfWeek = (workingHours?.daySettings || []).filter((daySetting) => {
    if (!daySetting || !daySetting.day) {
      return false;
    }

    return visibleDaysOfWeek.includes(daySetting.day);
  });

  const workingHourBoundsForVisibleDays = parseWorkingHourBounds(daySettingsForVisibleDaysOfWeek);
  const workingHourBounds = workingHourBoundsForVisibleDays;
  const [startWorkingHours, endWorkingHours] = workingHourBounds || [0, 24];

  return (
    <div className="cw-absolute cw-h-full cw-w-full cw-pointer-events-none cw-z-50">
      {[startWorkingHours, endWorkingHours].map((i) => {
        const top = i === 0 ? -1 : `${(100 * i) / 24}%`;
        return (
          <div
            key={`work-hour-bound-for-${visibleDays.join("-")}-${i}`}
            className="cw-absolute cw-border-b-2 cw-border-solid cw-border-b-[pink] cw-right-0 cw-left-0"
            style={{ top }}
          />
        );
      })}
    </div>
  );
};

const CalendarHorizontalGuideLines = () => (
  <div className="cw-h-full cw-w-full cw-absolute cw-pointer-events-none">
    {new Array(25).fill(null).map((_n, i) => {
      const top = i === 0 ? -1 : `${(100 * i) / 24}%`;
      return (
        <div
          key={i}
          className="cw-absolute cw-border-b cw-border-solid cw-border-b-subtle cw-right-0 cw-left-0"
          style={{ top }}
        />
      );
    })}
  </div>
);

const CalendarVerticalGuideLines = ({ dateTimes }: { dateTimes: DateTime[] }) => {
  const positionables = useMemo(
    () =>
      dateTimes.map((dateTime) => ({
        key: dateTime.toISO(),
        interval: Interval.fromDateTimes(dateTime.startOf("day"), dateTime.endOf("day")),
        render: () => (
          <section
            aria-label={`Events on ${dateTime.toLocaleString(DateTime.DATETIME_FULL)}`}
            className="cw-h-full cw-w-full cw-border-r cw-border-solid cw-border-r-subtle"
          ></section>
        ),
      })),
    [dateTimes],
  );

  if (dateTimes.length <= 1) {
    return null;
  }

  return (
    <div className="cw-pointer-events-none">
      <CalendarPositioner dateTimes={dateTimes} positionables={positionables} />
    </div>
  );
};
